import { useEffect, useRef, useState, useCallback } from "react";
import { createChart, ColorType, CrosshairMode } from "lightweight-charts";
import { getProvider, fetchAlpacaCandles, fetchCoinbaseCandles, ALPACA_DISPLAY_LOOKBACK_DAYS, formatCandles } from "./candles";
import { granularityMap, normalizeCandles } from "./chartHelpers";
import { computeMoreInfoIndicators } from "./tradeEngine";
import { computeVolumeProfile, VolumeProfilePrimitive } from "./volumeProfile";
import { fetchDrawings, insertDrawing, deleteDrawing } from "./drawingsStore";
import { useMeasureTool, MeasureOverlay } from "./measureTool";
import { useTrendLineTool, TrendLineOverlay } from "./trendLineTool";
import { useFibTool, FibOverlay } from "./fibTool";
import { useChannelTool, ChannelOverlay } from "./channelTool";
import { useRayTool, RayOverlay } from "./rayTool";
import { useLongPositionTool, LongPositionOverlay } from "./longPositionTool";
import { supabase } from "./supabaseClient";

// Candlestick chart with optional RSI + MACD subpanels. The price pane mirrors
// the More Info price chart; the RSI/MACD subpanels, bidirectional logical-range
// pane-sync, and synced crosshair are ported verbatim from the More Info create
// effect (App.js), adapted to be self-contained and TOGGLE-GATED: the RSI/MACD
// chart instances + sync + crosshair subscriptions are built ONLY when their
// toggle is on, and torn down when toggled off. No S/R, FVG, entry/exit, markers,
// or volume. Props: symbol ("BTC-USD" | "AAPL"), interval ("1h"|"6h"|"1d"),
// showRSI / showMACD (default off — zero indicator-chart cost on load).
export default function SignalChart({ symbol, interval, showRSI = false, showMACD = false, showBB = false, showVolProfile = false, showLevels = false, levelCats = { support: true, resistance: true, fvg: true }, userId = null }) {
  const containerRef = useRef(null);   // price DOM container (mirrors moreInfoChartRef)
  const chartObjRef = useRef(null);    // price chart instance (mirrors moreInfoChartObjRef)
  const priceSeriesRef = useRef(null); // candlestick series, for S/R price-line draw + teardown
  const overlayPriceLinesRef = useRef([]); // S/R price-lines on the price series (asset_levels overlay)
  const overlayFvgSeriesRef = useRef([]);  // FVG baseline series on the price chart (asset_levels overlay)
  const lastTimeRef = useRef(null);    // last candle time, for FVG band right-edge (read by draw effect)
  const levelsDataRef = useRef(null);  // cached { support_resistance, fvgs } — sub-toggles redraw from this, no refetch
  // Build 3 nonces: chartReady bumps after each chart (re)build; levelsLoaded after each asset_levels
  // read completes. The decoupled DRAW effect keys on both so it can redraw from cache without a rebuild.
  const [chartReady, setChartReady] = useState(0);
  const [levelsLoaded, setLevelsLoaded] = useState(0);
  // Load-failure UI (stopgap for the rate-limit blank-chart bug — see
  // docs/known-issue-rate-limiting-blank-charts.md). loadError drives a visible
  // "couldn't load / Retry" overlay instead of a silent blank pane; retryTick is bumped
  // by the Retry button to re-run the fetch effect below (included in its deps).
  const [loadError, setLoadError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  // Drawing tools (v1: horizontal lines only, persisted per user × symbol × context
  // "dashboard" — see src/db/migrations/0005_chart_drawings.sql). myDrawings is this
  // symbol's saved lines; drawMode arms multi-line click-to-place (stays on until toggled
  // off); userDrawingLinesRef holds
  // the created price-line handles for teardown/redraw, same pattern as overlayPriceLinesRef.
  const [myDrawings, setMyDrawings] = useState([]); // [{id, price}]
  const [drawMode, setDrawMode] = useState(false);
  const userDrawingLinesRef = useRef([]);
  // Measure tool (v1: price/%/candle-count click-drag readout, persisted per user × symbol
  // × context "dashboard" via Supabase chart_measurements — see measureTool.js. Shared hook
  // so this isn't a second copy-pasted implementation on the More Info chart.
  const granularitySeconds = granularityMap[interval] || 3600;
  const { measureMode, setMeasureMode, measurement, stats: measureStats, clearMeasurement } = useMeasureTool({
    chartRef: chartObjRef, seriesRef: priceSeriesRef, containerRef, granularitySeconds,
    userId, symbol, context: "dashboard",
  });
  // Trend Line tool (v1 of the "advanced drawing tools" build: multi-point, persisted-list
  // click-drag lines, per user × symbol × context "dashboard" via Supabase
  // chart_advanced_drawings — see trendLineTool.js. Shared hook so this isn't a second
  // copy-pasted implementation on the More Info chart, same convention as Measure/Draw Line.
  const { trendLineMode, setTrendLineMode, myLines: myTrendLines, deleteTrendLine, projectedLines: trendProjectedLines } = useTrendLineTool({
    chartRef: chartObjRef, seriesRef: priceSeriesRef, containerRef,
    userId, symbol, context: "dashboard",
  });
  // Fib Retracement tool (v1: 2-point click-drag, persisted list, same interaction as
  // Trend Line — see fibTool.js).
  const { fibMode, setFibMode, myFibs, deleteFib, projectedFibs } = useFibTool({
    chartRef: chartObjRef, seriesRef: priceSeriesRef, containerRef,
    userId, symbol, context: "dashboard",
  });
  // Parallel Channel tool (v1: click-drag main line + one more click for the offset —
  // see channelTool.js for the 2-stage interaction).
  const { channelMode, setChannelMode, myChannels, deleteChannel, projectedChannels, phase: channelPhase } = useChannelTool({
    chartRef: chartObjRef, seriesRef: priceSeriesRef, containerRef,
    userId, symbol, context: "dashboard",
  });
  // Ray tool (v1: 2-point click-drag, persisted list, same interaction as Trend Line —
  // see rayTool.js. Line renders extended past the second anchor to the pane edge).
  const { rayMode, setRayMode, myRays, deleteRay, projectedRays, phase: rayPhase } = useRayTool({
    chartRef: chartObjRef, seriesRef: priceSeriesRef, containerRef,
    userId, symbol, context: "dashboard",
  });
  // Long Position tool (v1: click-drag entry+risk, target auto-derived at a fixed 2:1
  // reward:risk — see longPositionTool.js. Last of the original 4-tool plan).
  const { longMode, setLongMode, myPositions, deletePosition, projectedPositions } = useLongPositionTool({
    chartRef: chartObjRef, seriesRef: priceSeriesRef, containerRef,
    userId, symbol, context: "dashboard",
  });
  // Mutual exclusion — only one drawing/measure tool armed at a time. Arming any one of
  // them disarms all the others; clicking the already-armed tool's button turns it off
  // (armTool(null)). Each button's onClick below calls this instead of toggling its own
  // setXMode directly.
  const armTool = useCallback((tool) => {
    setMeasureMode(tool === "measure");
    setDrawMode(tool === "draw");
    setTrendLineMode(tool === "trend");
    setFibMode(tool === "fib");
    setChannelMode(tool === "channel");
    setRayMode(tool === "ray");
    setLongMode(tool === "long");
  }, [setMeasureMode, setDrawMode, setTrendLineMode, setFibMode, setChannelMode, setRayMode, setLongMode]);
  const rsiContainerRef = useRef(null);  // RSI subpanel container
  const rsiChartObjRef = useRef(null);   // RSI subpanel chart instance
  const macdContainerRef = useRef(null); // MACD subpanel container
  const macdChartObjRef = useRef(null);  // MACD subpanel chart instance
  const priceXhairRef = useRef(null);  // crosshair overlay div over the price pane
  const rsiXhairRef = useRef(null);    // crosshair overlay div over the RSI pane
  const macdXhairRef = useRef(null);   // crosshair overlay div over the MACD pane
  const rangeSyncRef = useRef(null);   // {paneKey: logical-range handler} map, for unsubscribe
  const xhairHandlersRef = useRef(null); // {paneKey: crosshair handler} map, for unsubscribe
  const priceLegendRef = useRef(null);        // OHLCV hover-legend div (top-left of price pane)
  const priceLegendHandlerRef = useRef(null); // price-pane legend crosshair handler, for unsubscribe
  const rsiLegendRef = useRef(null);          // RSI hover-legend div (top-left of RSI pane)
  const macdLegendRef = useRef(null);         // MACD hover-legend div (top-left of MACD pane)

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Real timeframes map to their own granularity; only a non-key falls back to 1h.
    const granularity = granularityMap[interval] || 3600;

    let chart = null;
    let cancelled = false;

    const run = async () => {
      try {
        if (!cancelled) setLoadError(null); // clear any prior failure on every (re)fetch
        let raw;
        if (getProvider(symbol) === "coinbase") {
          // fetchCoinbaseCandles pages backwards past Coinbase's 300-candle-per-request
          // cap for meaningfully more chart history (see candles.js COINBASE_PAGES).
          raw = await fetchCoinbaseCandles(symbol, granularity, true); // priority: user-facing chart
        } else {
          // fetchAlpacaCandles returns the SAME positional [time,low,high,open,close,volume] shape.
          // 4th arg = display-only lookback override (bigger window than the scoring loops use).
          raw = await fetchAlpacaCandles(symbol, granularity, undefined, ALPACA_DISPLAY_LOOKBACK_DAYS[granularity], true); // priority: user-facing chart
        }
        // Defensive: lightweight-charts requires ascending time. Coinbase + Alpaca
        // reach here via formatCandles' .reverse() in opposite native orders, so sort
        // explicitly rather than trust source order.
        const formatted = formatCandles(normalizeCandles(raw)).sort((a, b) => a.time - b.time);
        // Stash the last candle time for the decoupled overlay draw effect (FVG band right edge).
        lastTimeRef.current = formatted.length ? formatted[formatted.length - 1].time : null;
        // Indicators computed only when at least one subpanel/overlay is on (zero cost when off).
        const indicators = (showRSI || showMACD || showBB) ? computeMoreInfoIndicators(formatted) : null;
        if (cancelled || !containerRef.current) return;

        chart = createChart(el, {
          width: el.clientWidth,
          height: 420,
          layout: { background: { type: ColorType.Solid, color: "rgba(17,17,17,0.96)" }, textColor: "#aaa" },
          grid: { vertLines: { color: "rgba(255,255,255,0.05)" }, horzLines: { color: "rgba(255,255,255,0.05)" } },
          timeScale: { timeVisible: true, borderColor: "rgba(255,255,255,0.1)" },
          rightPriceScale: { borderColor: "rgba(255,255,255,0.1)", minimumWidth: 70 },
          crosshair: { mode: CrosshairMode.Normal },
        });
        chartObjRef.current = chart;
        // Volume histogram — overlay on its own (empty) price scale, pinned to the
        // bottom band. Created BEFORE the candlestick series so candles draw on top
        // (same "histogram first" ordering as the MACD subpanel). Reuses c.volume
        // already carried by formatCandles — no new fetch, no pipeline change.
        const volSeries = chart.addHistogramSeries({
          priceScaleId: "",
          priceFormat: { type: "volume" },
          lastValueVisible: false,
          priceLineVisible: false,
        });
        chart.priceScale("").applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
        volSeries.setData(
          formatted.map((c) => ({
            time: c.time,
            value: c.volume,
            color: c.close >= c.open ? "rgba(74, 222, 128, 0.5)" : "rgba(248, 113, 113, 0.5)",
          }))
        );
        const series = chart.addCandlestickSeries({ priceLineColor: "#F0F0F0" });
        priceSeriesRef.current = series;
        series.setData(
          formatted.map((c) => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close }))
        );

        // --- Bollinger Bands overlay — 3 line series drawn directly on the price pane
        // (upper/lower share a color, middle is the SMA). Padded to full candle length so
        // gaps show as whitespace rather than a truncated line, same convention as RSI/MACD. ---
        const bbData = indicators?.bollinger || [];
        if (showBB && bbData.length) {
          const bbByTime = new Map(bbData.map((p) => [p.time, p]));
          const paddedBand = (key) => formatted.map((c) =>
            bbByTime.has(c.time) ? { time: c.time, value: bbByTime.get(c.time)[key] } : { time: c.time }
          );
          const bbUpper = chart.addLineSeries({ color: "rgba(129,140,248,0.7)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
          const bbMiddle = chart.addLineSeries({ color: "rgba(255,255,255,0.35)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
          const bbLower = chart.addLineSeries({ color: "rgba(129,140,248,0.7)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
          bbUpper.setData(paddedBand("upper"));
          bbMiddle.setData(paddedBand("middle"));
          bbLower.setData(paddedBand("lower"));
        }

        // --- Volume Profile overlay — right-anchored horizontal histogram of volume by
        // price level, drawn via a custom primitive attached to the candlestick series
        // (lightweight-charts has no built-in volume-profile series type). ---
        if (showVolProfile && formatted.length) {
          const profile = computeVolumeProfile(formatted, 24);
          const vpPrimitive = new VolumeProfilePrimitive(() => profile);
          series.attachPrimitive(vpPrimitive);
          vpPrimitive.updateAllViews();
        }

        // Load zoomed to the RECENT candles (founder ask 2026-07-09): last ~90 with a
        // small right margin, instead of fitContent's full-history view. Short feeds
        // fall back to fitContent. User can still scroll/zoom out to full history.
        if (formatted.length > 95) {
          chart.timeScale().setVisibleLogicalRange({ from: formatted.length - 90, to: formatted.length + 6 });
        } else {
          chart.timeScale().fitContent();
        }
        // Build 3: signal that the chart + price series exist so the decoupled overlay DRAW
        // effect can run. The asset_levels read + draw now live in their own effects (below).
        if (!cancelled) setChartReady((n) => n + 1);

        // --- RSI subpanel — created ONLY when showRSI (mirrors More Info exactly) ---
        const rsiEl = rsiContainerRef.current;
        const rsiSeriesData = indicators?.rsi || [];
        if (showRSI && rsiEl && rsiSeriesData.length) {
          const rsiChart = createChart(rsiEl, {
            width: rsiEl.clientWidth,
            height: 180,
            layout: { background: { type: ColorType.Solid, color: "rgba(17,17,17,0.96)" }, textColor: "#aaa" },
            grid: { vertLines: { color: "rgba(255,255,255,0.04)" }, horzLines: { color: "rgba(255,255,255,0.04)" } },
            timeScale: { timeVisible: true, borderColor: "rgba(255,255,255,0.1)" },
            rightPriceScale: { borderColor: "rgba(255,255,255,0.1)", minimumWidth: 70 },
            crosshair: { mode: CrosshairMode.Normal },
          });
          rsiChartObjRef.current = rsiChart;

          // PAD to full candle length so logical indexes match the price chart exactly.
          const rsiByTime = new Map(rsiSeriesData.map((p) => [p.time, p.value]));
          const paddedRsi = formatted.map((c) =>
            rsiByTime.has(c.time) ? { time: c.time, value: rsiByTime.get(c.time) } : { time: c.time }
          );

          const rsiLine = rsiChart.addLineSeries({ color: "#818cf8", lineWidth: 2, priceLineVisible: false, lastValueVisible: true });
          rsiLine.setData(paddedRsi);

          // 30 / 70 guide lines (standard RSI overbought/oversold)
          rsiLine.createPriceLine({ price: 70, color: "rgba(248,113,113,0.5)", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "70" });
          rsiLine.createPriceLine({ price: 30, color: "rgba(74,222,128,0.5)", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "30" });

          rsiChart.timeScale().fitContent();
        }

        // --- MACD subpanel — created ONLY when showMACD (mirrors More Info exactly) ---
        const macdEl = macdContainerRef.current;
        const macdSeriesData = indicators?.macd || [];
        if (showMACD && macdEl && macdSeriesData.length) {
          const macdChart = createChart(macdEl, {
            width: macdEl.clientWidth,
            height: 180,
            layout: { background: { type: ColorType.Solid, color: "rgba(17,17,17,0.96)" }, textColor: "#aaa" },
            grid: { vertLines: { color: "rgba(255,255,255,0.04)" }, horzLines: { color: "rgba(255,255,255,0.04)" } },
            timeScale: { timeVisible: true, borderColor: "rgba(255,255,255,0.1)" },
            rightPriceScale: { borderColor: "rgba(255,255,255,0.1)", minimumWidth: 70 },
            crosshair: { mode: CrosshairMode.Normal },
          });
          macdChartObjRef.current = macdChart;

          // PAD each series to full candle length so logical indexes match the price chart.
          const macdByTime = new Map(macdSeriesData.map((p) => [p.time, p]));
          const paddedMacdLine = formatted.map((c) =>
            macdByTime.has(c.time) ? { time: c.time, value: macdByTime.get(c.time).macd } : { time: c.time }
          );
          const paddedSignalLine = formatted.map((c) =>
            macdByTime.has(c.time) ? { time: c.time, value: macdByTime.get(c.time).signal } : { time: c.time }
          );
          const paddedHistogram = formatted.map((c) => {
            if (!macdByTime.has(c.time)) return { time: c.time };
            const h = macdByTime.get(c.time).histogram;
            return { time: c.time, value: h, color: h >= 0 ? "rgba(74,222,128,0.5)" : "rgba(248,113,113,0.5)" };
          });

          // Histogram FIRST so the lines render on top of it.
          const macdHist = macdChart.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false,
            priceFormat: { type: "custom", minMove: 0.0000001, formatter: (p) => {
              const a = Math.abs(p);
              if (a === 0) return "0";
              if (a >= 1) return p.toFixed(2);
              if (a >= 0.01) return p.toFixed(4);
              return p.toPrecision(3);
            } } });
          macdHist.setData(paddedHistogram);

          const macdLineSeries = macdChart.addLineSeries({ color: "#818cf8", lineWidth: 2, priceLineVisible: false, lastValueVisible: true,
            priceFormat: { type: "custom", minMove: 0.0000001, formatter: (p) => {
              const a = Math.abs(p);
              if (a === 0) return "0";
              if (a >= 1) return p.toFixed(2);
              if (a >= 0.01) return p.toFixed(4);
              return p.toPrecision(3);
            } } });
          macdLineSeries.setData(paddedMacdLine);

          const signalLineSeries = macdChart.addLineSeries({ color: "#f97316", lineWidth: 2, priceLineVisible: false, lastValueVisible: true,
            priceFormat: { type: "custom", minMove: 0.0000001, formatter: (p) => {
              const a = Math.abs(p);
              if (a === 0) return "0";
              if (a >= 1) return p.toFixed(2);
              if (a >= 0.01) return p.toFixed(4);
              return p.toPrecision(3);
            } } });
          signalLineSeries.setData(paddedSignalLine);

          // Zero guide line, faint white (mirrors the RSI 30/70 guide treatment).
          macdLineSeries.createPriceLine({ price: 0, color: "rgba(255,255,255,0.25)", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "0" });

          macdChart.timeScale().fitContent();
        }

        // --- Pane registry: price always; RSI/MACD only if they were created. ---
        const panes = [];
        panes.push({ key: "price", chart: chartObjRef.current, xhairDiv: priceXhairRef.current });
        if (rsiChartObjRef.current) {
          panes.push({ key: "rsi", chart: rsiChartObjRef.current, xhairDiv: rsiXhairRef.current });
        }
        if (macdChartObjRef.current) {
          panes.push({ key: "macd", chart: macdChartObjRef.current, xhairDiv: macdXhairRef.current });
        }

        // --- Price-scale width parity (synced-crosshair alignment) ---
        // The crosshair x is computed per-pane via each pane's OWN timeScale.timeToCoordinate,
        // so unequal right-price-scale widths (wide price labels vs narrow RSI 30/70 vs tiny
        // MACD decimals) map the same timestamp to different x → the lines diverge across panes.
        // minimumWidth in the createChart config is only a FLOOR (70) and is exceeded by the
        // price pane's labels, so the panes' widths diverge. There's no single safe fixed width
        // (label width is asset-dependent), so measure each pane's actual right-scale width and
        // raise every pane's minimumWidth to the max — the documented vertical-stack-alignment
        // pattern (lightweight-charts v4.2 PriceScaleOptions.minimumWidth), made asset-agnostic.
        // Deferred one frame because price-scale width is computed on the next paint; the
        // existing `cancelled` guard makes a late callback safe (charts may be removed by then).
        if (panes.length > 1) {
          requestAnimationFrame(() => {
            if (cancelled) return;
            let maxScaleWidth = 0;
            for (const p of panes) {
              try {
                const w = p.chart.priceScale("right").width();
                if (w > maxScaleWidth) maxScaleWidth = w;
              } catch (e) {}
            }
            if (maxScaleWidth > 0) {
              for (const p of panes) {
                try { p.chart.priceScale("right").applyOptions({ minimumWidth: maxScaleWidth }); } catch (e) {}
              }
            }
          });
        }

        // Bidirectional logical-range sync: any pane drives the others ("move in unison").
        // Echo-suppressed via lastApplied + epsilon (the VisibleLogicalRange event fires
        // async; record the last range applied per pane and skip echo/no-op sets).
        const lastApplied = {};  // key -> {from, to} most recently set on that pane's timeScale
        const EPS = 0.0001;      // logical-range units; tolerant of float round-trip drift
        const nearlyEqual = (a, b) =>
          a && b && Math.abs(a.from - b.from) < EPS && Math.abs(a.to - b.to) < EPS;

        const rangeHandlers = {};
        for (const source of panes) {
          const handler = (range) => {
            if (range == null) return;
            for (const target of panes) {
              if (target.key === source.key) continue;
              if (nearlyEqual(lastApplied[target.key], range)) continue;  // echo / no-op: skip
              lastApplied[target.key] = { from: range.from, to: range.to };
              target.chart.timeScale().setVisibleLogicalRange(range);
            }
          };
          rangeHandlers[source.key] = handler;
          source.chart.timeScale().subscribeVisibleLogicalRangeChange(handler);
        }
        rangeSyncRef.current = rangeHandlers;

        // --- Synced crosshair (custom DOM overlay; hovering one pane draws a faint
        // vertical line on the others at the same time). Echo-free via reentrancy guard. ---
        let xhairSyncing = false;

        const drawXhair = (targetChart, lineDiv, time) => {
          if (!targetChart || !lineDiv) return;
          if (time == null) { lineDiv.style.display = "none"; return; }
          const x = targetChart.timeScale().timeToCoordinate(time);
          if (x == null) { lineDiv.style.display = "none"; return; }
          lineDiv.style.left = x + "px";
          lineDiv.style.display = "block";
        };

        // Handler factory hoisted OUT of the loop (one declaration, not per-iteration)
        // so no-loop-func doesn't flag the shared `xhairSyncing` closure. Behavior is
        // identical: same single guard, same per-pane subscription, same stored map.
        const makeXhairHandler = (source) => (param) => {
          if (xhairSyncing) return;
          xhairSyncing = true;
          for (const target of panes) {
            if (target.key === source.key) continue;
            drawXhair(target.chart, target.xhairDiv, param.time);
          }
          xhairSyncing = false;
        };

        const xhairHandlers = {};
        for (const source of panes) {
          const handler = makeXhairHandler(source);
          xhairHandlers[source.key] = handler;
          source.chart.subscribeCrosshairMove(handler);
        }
        xhairHandlersRef.current = xhairHandlers;

        // --- Hover legends (price + RSI/MACD panes) — top-left, updated via DIRECT DOM on crosshair
        // move (NOT React state; a per-move setState would re-render the app). ONE shared handler
        // (subscribed to every pane) drives ALL legends off the hovered param.time; the time axes are
        // synced, so a time from any pane resolves in each per-indicator by-time Map. Free crosshair
        // (CrosshairMode.Normal) can land between bars → each legend falls back to its LAST value so
        // something always shows once data loads. RSI/MACD legends update ONLY when their pane exists
        // (toggled on); everything null-guarded. Torn down in the same teardown block. ---
        const legendEl = priceLegendRef.current;
        if (legendEl && formatted.length) {
          const lab = (t) => `<span style="color:#666">${t}</span>`;
          const val = (t) => `<span style="color:#ddd">${t}</span>`;
          const SEP = `<span style="color:#555">&nbsp;&nbsp;·&nbsp;&nbsp;</span>`;
          const fmt2 = (v) => (Number.isFinite(v) ? v.toFixed(2) : "—");
          const fmtPrice = (v) => {
            if (!Number.isFinite(v)) return "—";
            const a = Math.abs(v);
            const dp = a >= 100 ? 2 : a >= 1 ? 3 : a >= 0.01 ? 5 : 8;
            return v.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
          };
          const fmtVol = (v) => {
            if (!Number.isFinite(v)) return "—";
            const a = Math.abs(v);
            if (a >= 1e9) return (v / 1e9).toFixed(1) + "B";
            if (a >= 1e6) return (v / 1e6).toFixed(1) + "M";
            if (a >= 1e3) return (v / 1e3).toFixed(0) + "K";
            return String(Math.round(v));
          };

          // PRICE legend — full-word labels, thousands-separated prices, ·-separated pairs.
          const legendByTime = new Map(
            formatted.map((c, i) => [c.time, { c, prev: i > 0 ? formatted[i - 1] : null }])
          );
          const lastEntry = {
            c: formatted[formatted.length - 1],
            prev: formatted.length > 1 ? formatted[formatted.length - 2] : null,
          };
          const renderPrice = ({ c, prev }) => {
            const pct = prev
              ? ((c.close - prev.close) / prev.close) * 100
              : ((c.close - c.open) / c.open) * 100;
            const pctColor = pct >= 0 ? "#4ADE80" : "#F87171";
            const pctStr = (pct >= 0 ? "+" : "") + pct.toFixed(2) + "%";
            // Value colors: High always green, Low always red, Close by this candle's direction,
            // Open by the PREVIOUS candle's direction (fall back to this candle on the first bar).
            const GREEN = "#4ADE80", RED = "#F87171";
            const closeColor = c.close >= c.open ? GREEN : RED;
            const openColor = prev ? (prev.close >= prev.open ? GREEN : RED) : closeColor;
            const cval = (color, t) => `<span style="color:${color}">${t}</span>`;
            legendEl.innerHTML =
              `${lab("Open")} ${cval(openColor, fmtPrice(c.open))}${SEP}${lab("High")} ${cval(GREEN, fmtPrice(c.high))}${SEP}` +
              `${lab("Low")} ${cval(RED, fmtPrice(c.low))}${SEP}${lab("Close")} ${cval(closeColor, fmtPrice(c.close))}${SEP}` +
              `${lab("Volume")} ${val(fmtVol(c.volume))}${SEP}<span style="color:${pctColor}">${pctStr}</span>`;
          };

          // RSI legend — only when the RSI pane exists this run (rsiSeriesData = [{time,value}]).
          let renderRsi = null;
          if (rsiChartObjRef.current && rsiLegendRef.current && rsiSeriesData.length) {
            const rsiLegendEl = rsiLegendRef.current;
            const rsiMap = new Map(rsiSeriesData.map((p) => [p.time, p.value]));
            const rsiLast = rsiSeriesData[rsiSeriesData.length - 1];
            renderRsi = (time) => {
              const v = time != null && rsiMap.has(time) ? rsiMap.get(time) : rsiLast.value;
              rsiLegendEl.innerHTML = `${lab("RSI")} <span style="color:#818cf8">${fmt2(v)}</span>`;
            };
          }

          // MACD legend — only when the MACD pane exists this run (macdSeriesData = [{time,macd,signal,histogram}]).
          let renderMacd = null;
          if (macdChartObjRef.current && macdLegendRef.current && macdSeriesData.length) {
            const macdLegendEl = macdLegendRef.current;
            const macdMap = new Map(macdSeriesData.map((p) => [p.time, p]));
            const macdLast = macdSeriesData[macdSeriesData.length - 1];
            renderMacd = (time) => {
              const m = time != null && macdMap.has(time) ? macdMap.get(time) : macdLast;
              const histColor = m.histogram >= 0 ? "#4ADE80" : "#F87171";
              macdLegendEl.innerHTML =
                `${lab("MACD")} <span style="color:#818cf8">${fmt2(m.macd)}</span>${SEP}${lab("Signal")} <span style="color:#f97316">${fmt2(m.signal)}</span>${SEP}` +
                `${lab("Hist")} <span style="color:${histColor}">${fmt2(m.histogram)}</span>`;
            };
          }

          // Initial paint (last values), then ONE handler updates all present legends on crosshair move.
          renderPrice(lastEntry);
          if (renderRsi) renderRsi(null);
          if (renderMacd) renderMacd(null);
          const legendHandler = (param) => {
            const t = param && param.time != null ? param.time : null;
            const entry = t != null && legendByTime.has(t) ? legendByTime.get(t) : lastEntry;
            renderPrice(entry);
            if (renderRsi) renderRsi(t);
            if (renderMacd) renderMacd(t);
          };
          priceLegendHandlerRef.current = legendHandler;
          // Subscribe the SAME handler to EVERY pane (price + RSI/MACD when present) so hovering any
          // pane updates the legends — the time axes are synced, so param.time from any pane resolves
          // in the per-indicator Maps. Mirrors the synced-crosshair pane loop; `panes` holds only the
          // charts that were actually created (inherently null-safe).
          for (const p of panes) {
            try { p.chart.subscribeCrosshairMove(legendHandler); } catch (e) {}
          }
        }
      } catch (error) {
        console.error("SignalChart fetch/render failed:", error);
        if (!cancelled) {
          const status = error?.response?.status;
          setLoadError(status === 429 ? "Rate limited — try again shortly" : "Couldn't load chart");
        }
      }
    };
    run();

    return () => {
      cancelled = true;
      // (1) Synced-crosshair teardown — unsubscribe every pane's handler BEFORE any remove.
      if (xhairHandlersRef.current) {
        const h = xhairHandlersRef.current;
        const chartByKey = { price: chart, rsi: rsiChartObjRef.current, macd: macdChartObjRef.current };
        for (const key of Object.keys(h)) {
          const c = chartByKey[key];
          try { if (c) c.unsubscribeCrosshairMove(h[key]); } catch (e) {}
        }
        xhairHandlersRef.current = null;
      }
      // (1b) OHLCV legend crosshair teardown — unsubscribe the ONE legend handler from EVERY pane it
      // was subscribed to (price + RSI/MACD when present), each null-guarded, BEFORE any chart.remove()
      // below. Mirrors the synced-crosshair chartByKey teardown.
      if (priceLegendHandlerRef.current) {
        const legendChartByKey = { price: chart, rsi: rsiChartObjRef.current, macd: macdChartObjRef.current };
        for (const key of Object.keys(legendChartByKey)) {
          const c = legendChartByKey[key];
          try { if (c) c.unsubscribeCrosshairMove(priceLegendHandlerRef.current); } catch (e) {}
        }
        priceLegendHandlerRef.current = null;
      }
      // (2) Bidirectional range-sync teardown — unsubscribe each pane's handler from its
      // OWN timeScale BEFORE any chart.remove().
      if (rangeSyncRef.current) {
        const rh = rangeSyncRef.current;
        const chartByKey = { price: chart, rsi: rsiChartObjRef.current, macd: macdChartObjRef.current };
        for (const key of Object.keys(rh)) {
          const c = chartByKey[key];
          try { if (c) c.timeScale().unsubscribeVisibleLogicalRangeChange(rh[key]); } catch (e) {}
        }
        rangeSyncRef.current = null;
      }
      // (3) RSI instance — guarded (may not exist when showRSI was off).
      if (rsiChartObjRef.current) {
        try { rsiChartObjRef.current.remove(); } catch (e) {}
        rsiChartObjRef.current = null;
      }
      // (4) MACD instance — guarded (may not exist when showMACD was off).
      if (macdChartObjRef.current) {
        try { macdChartObjRef.current.remove(); } catch (e) {}
        macdChartObjRef.current = null;
      }
      // (5) Price instance — always present. chart.remove() frees all overlay price-lines /
      // baseline series on it; the DRAW effect resets the overlay ref arrays on its next run.
      if (chart) chart.remove();
      chartObjRef.current = null;
      priceSeriesRef.current = null;
    };
  }, [symbol, interval, showRSI, showMACD, showBB, showVolProfile, retryTick]);

  // Build 3: READ effect — caches asset_levels for [symbol, interval] into levelsDataRef WITHOUT
  // touching candles or the chart. Bumps levelsLoaded so the DRAW effect redraws from cache.
  useEffect(() => {
    let cancelled = false;
    // Clear stale cache before any refetch so a chart rebuild on symbol/interval change can't
    // draw against the PREVIOUS symbol's cached row (eliminates the brief stale-overlay flash).
    levelsDataRef.current = null;
    if (!showLevels) { setLevelsLoaded((n) => n + 1); return; }
    (async () => {
      try {
        const { data } = await supabase.from("asset_levels")
          .select("support_resistance, fvgs").eq("symbol", symbol).eq("timeframe", interval).maybeSingle();
        if (!cancelled) { levelsDataRef.current = data || null; setLevelsLoaded((n) => n + 1); }
      } catch (e) {
        console.log("[asset_levels-read]", e?.message || e);
        if (!cancelled) { levelsDataRef.current = null; setLevelsLoaded((n) => n + 1); }
      }
    })();
    return () => { cancelled = true; };
  }, [symbol, interval, showLevels]);

  // Build 3: DRAW effect — redraws S/R + FVG overlays from the CACHED row (levelsDataRef) on every
  // visibility change, WITHOUT refetching candles or rebuilding the chart. Always tears down prior
  // overlays first (idempotent), then draws each category gated on levelCats.*. Keyed on the
  // chart-ready / levels-loaded nonces and the three per-category flags.
  useEffect(() => {
    // Teardown prior overlays (idempotent; stale handles after a chart rebuild are harmless here).
    overlayPriceLinesRef.current.forEach((pl) => { try { priceSeriesRef.current?.removePriceLine(pl); } catch (e) {} });
    overlayPriceLinesRef.current = [];
    overlayFvgSeriesRef.current.forEach((s) => { try { chartObjRef.current?.removeSeries(s); } catch (e) {} });
    overlayFvgSeriesRef.current = [];

    const chart = chartObjRef.current;
    const series = priceSeriesRef.current;
    const levelRow = levelsDataRef.current;
    if (!showLevels || !levelRow || !chart || !series) return;  // not ready / nothing to draw

    const sr = levelRow.support_resistance;
    const fvgs = levelRow.fvgs || [];
    const rightTime = lastTimeRef.current;

    // S/R price-lines — support #4ade80, resistance #ea580c (per-category gated).
    if (sr) {
      if (levelCats.support) {
        (sr.support || []).forEach((z, i) => {
          const key = i === 0 ? "S" : `S${i + 1}`;
          const pl = series.createPriceLine({
            price: z.priceMid, color: "#4ade80", lineWidth: 1, lineStyle: 2,
            axisLabelVisible: true, title: key,
          });
          overlayPriceLinesRef.current.push(pl);
        });
      }
      if (levelCats.resistance) {
        (sr.resistance || []).forEach((z, i) => {
          const key = i === 0 ? "R" : `R${i + 1}`;
          const pl = series.createPriceLine({
            price: z.priceMid, color: "#ea580c", lineWidth: 1, lineStyle: 2,
            axisLabelVisible: true, title: key,
          });
          overlayPriceLinesRef.current.push(pl);
        });
      }
    }

    // FVG bands — gold baseline series (unfilled, sizePct>=0.3, nearest-first), per-category gated.
    if (levelCats.fvg) {
      const fvgPool = fvgs
        .filter((g) => !g.filled && g.sizePct >= 0.3)
        .map((g) => {
          const m = (g.top + g.bottom) / 2;
          const cp = sr?.currentPrice ?? g.top;
          return { ...g, _d: Math.abs((m - cp) / cp) * 100 };
        })
        .sort((a, b) => a._d - b._d);

      if (rightTime != null) {
        fvgPool.forEach((g, i) => {
          const key = `F${i + 1}`;
          const bandSeries = chart.addBaselineSeries({
            baseValue: { type: "price", price: g.bottom },
            topFillColor1: "rgba(250, 204, 21, 0.18)",
            topFillColor2: "rgba(250, 204, 21, 0.18)",
            topLineColor: "rgba(250, 204, 21, 0.45)",
            bottomFillColor1: "rgba(0,0,0,0)",
            bottomFillColor2: "rgba(0,0,0,0)",
            bottomLineColor: "rgba(0,0,0,0)",
            lineWidth: 1,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
          });
          const t0 = g.time;
          if (t0 == null || t0 > rightTime) {
            bandSeries.setData([{ time: rightTime, value: g.top }]);
          } else {
            bandSeries.setData([
              { time: t0, value: g.top },
              { time: rightTime, value: g.top },
            ]);
            try {
              bandSeries.setMarkers([{
                time: t0, position: "aboveBar", color: "#fbbf24",
                shape: "circle", text: key,
              }]);
            } catch (e) {}
          }
          overlayFvgSeriesRef.current.push(bandSeries);
        });
      }
    }
  }, [chartReady, levelsLoaded, showLevels, levelCats.support, levelCats.resistance, levelCats.fvg]);

  // Drawing tools READ effect — fetches this user's saved horizontal lines for
  // [symbol, "dashboard"] context. Cleared before refetch so a symbol change can't
  // briefly show the PREVIOUS symbol's lines.
  useEffect(() => {
    let cancelled = false;
    setMyDrawings([]);
    if (!userId) return;
    fetchDrawings(userId, symbol, "dashboard")
      .then((rows) => { if (!cancelled) setMyDrawings(rows); })
      .catch((e) => console.error("[chart_drawings-read]", e?.message || e));
    return () => { cancelled = true; };
  }, [userId, symbol]);

  // Drawing tools DRAW effect — redraws saved lines from myDrawings on every change,
  // same teardown-then-draw pattern as the S/R/FVG effect above.
  useEffect(() => {
    userDrawingLinesRef.current.forEach((pl) => { try { priceSeriesRef.current?.removePriceLine(pl); } catch (e) {} });
    userDrawingLinesRef.current = [];
    const series = priceSeriesRef.current;
    if (!series) return;
    myDrawings.forEach((d) => {
      const pl = series.createPriceLine({
        price: d.price, color: "#f97316", lineWidth: 1, lineStyle: 0,
        axisLabelVisible: true, title: "drawn",
      });
      userDrawingLinesRef.current.push(pl);
    });
  }, [chartReady, myDrawings]);

  // Drawing tools CLICK-TO-PLACE effect — armed while drawMode is on. Stays armed across
  // multiple clicks (multi-line mode) so the user can place several lines in a row; only
  // disarms when they click "Draw Line" again to turn it off.
  useEffect(() => {
    const chart = chartObjRef.current;
    const series = priceSeriesRef.current;
    if (!drawMode || !chart || !series || !userId) return;
    const handleClick = (param) => {
      if (!param.point) return;
      const price = series.coordinateToPrice(param.point.y);
      if (price == null) return;
      insertDrawing(userId, symbol, "dashboard", price)
        .then((row) => setMyDrawings((prev) => [...prev, row]))
        .catch((e) => console.error("[chart_drawings-insert]", e?.message || e));
    };
    chart.subscribeClick(handleClick);
    return () => { try { chart.unsubscribeClick(handleClick); } catch (e) {} };
  }, [drawMode, chartReady, userId, symbol]);

  const xhairStyle = {
    position: "absolute", top: 0, bottom: 0, width: "1px",
    background: "rgba(255,255,255,0.25)", pointerEvents: "none", display: "none", left: 0, zIndex: 3,
  };

  // OHLCV hover-legend style — top-left inside the price pane, above the canvas, click-through.
  const priceLegendStyle = {
    position: "absolute", top: "8px", left: "8px", zIndex: 4, pointerEvents: "none",
    font: "12px system-ui", background: "rgba(10,10,10,0.7)", borderRadius: "4px",
    padding: "4px 8px", whiteSpace: "nowrap",
  };

  return (
    <>
      {/* Drawing tools + Measure toolbar. Both need a signed-in user — each is persisted per
          user × symbol × context via Supabase (chart_drawings / chart_measurements), synced
          across devices, not localStorage. "Draw Line" arms multi-line click-to-place; each
          saved line shows as a chip with its price + a delete ×. "Measure" arms a click-drag
          readout of price/%/candle-count between two points; one saved measurement per
          symbol, with a small × on the box to clear it. */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
        {userId && (
          <button
            onClick={() => armTool(measureMode ? null : "measure")}
            style={{
              minHeight: "32px", padding: "0 12px", borderRadius: "6px", cursor: "pointer",
              fontSize: "12px", fontWeight: 600,
              background: measureMode ? "rgba(129,140,248,0.18)" : "rgba(255,255,255,0.04)",
              border: measureMode ? "1px solid #818cf8" : "1px solid rgba(255,255,255,0.1)",
              color: measureMode ? "#818cf8" : "#aaa",
            }}
          >
            {measureMode ? "Drag to measure…" : "Measure"}
          </button>
        )}
        {userId && (
          <button
            onClick={() => armTool(drawMode ? null : "draw")}
            style={{
              minHeight: "32px", padding: "0 12px", borderRadius: "6px", cursor: "pointer",
              fontSize: "12px", fontWeight: 600,
              background: drawMode ? "rgba(249,115,22,0.18)" : "rgba(255,255,255,0.04)",
              border: drawMode ? "1px solid #f97316" : "1px solid rgba(255,255,255,0.1)",
              color: drawMode ? "#f97316" : "#aaa",
            }}
          >
            {drawMode ? "Click to place (click to stop)" : "Draw Line"}
          </button>
        )}
        {userId && (
          <button
            onClick={() => armTool(trendLineMode ? null : "trend")}
            style={{
              minHeight: "32px", padding: "0 12px", borderRadius: "6px", cursor: "pointer",
              fontSize: "12px", fontWeight: 600,
              background: trendLineMode ? "rgba(249,115,22,0.18)" : "rgba(255,255,255,0.04)",
              border: trendLineMode ? "1px solid #f97316" : "1px solid rgba(255,255,255,0.1)",
              color: trendLineMode ? "#f97316" : "#aaa",
            }}
          >
            {trendLineMode ? "Drag to draw (click to stop)" : "Trend Line"}
          </button>
        )}
        {userId && myTrendLines.map((l, i) => (
            <span key={l.id} style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
              color: "#ccc", fontSize: "12px",
            }}>
              Trend {i + 1}
              <button
                aria-label="Delete trend line"
                onClick={() => deleteTrendLine(l.id)}
                style={{
                  background: "none", border: "none", color: "#888", cursor: "pointer",
                  fontSize: "13px", lineHeight: 1, padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        {userId && (
          <button
            onClick={() => armTool(fibMode ? null : "fib")}
            style={{
              minHeight: "32px", padding: "0 12px", borderRadius: "6px", cursor: "pointer",
              fontSize: "12px", fontWeight: 600,
              background: fibMode ? "rgba(234,179,8,0.18)" : "rgba(255,255,255,0.04)",
              border: fibMode ? "1px solid #eab308" : "1px solid rgba(255,255,255,0.1)",
              color: fibMode ? "#eab308" : "#aaa",
            }}
          >
            {fibMode ? "Drag to draw (click to stop)" : "Fib Retracement"}
          </button>
        )}
        {userId && myFibs.map((f, i) => (
            <span key={f.id} style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
              color: "#ccc", fontSize: "12px",
            }}>
              Fib {i + 1}
              <button
                aria-label="Delete fib retracement"
                onClick={() => deleteFib(f.id)}
                style={{
                  background: "none", border: "none", color: "#888", cursor: "pointer",
                  fontSize: "13px", lineHeight: 1, padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        {userId && (
          <button
            onClick={() => armTool(channelMode ? null : "channel")}
            style={{
              minHeight: "32px", padding: "0 12px", borderRadius: "6px", cursor: "pointer",
              fontSize: "12px", fontWeight: 600,
              background: channelMode ? "rgba(129,140,248,0.18)" : "rgba(255,255,255,0.04)",
              border: channelMode ? "1px solid #818cf8" : "1px solid rgba(255,255,255,0.1)",
              color: channelMode ? "#818cf8" : "#aaa",
            }}
          >
            {!channelMode ? "Parallel Channel" : channelPhase === "awaiting-offset" ? "Click to set width" : "Drag to draw (click to stop)"}
          </button>
        )}
        {userId && myChannels.map((c, i) => (
            <span key={c.id} style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
              color: "#ccc", fontSize: "12px",
            }}>
              Channel {i + 1}
              <button
                aria-label="Delete channel"
                onClick={() => deleteChannel(c.id)}
                style={{
                  background: "none", border: "none", color: "#888", cursor: "pointer",
                  fontSize: "13px", lineHeight: 1, padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        {userId && (
          <button
            onClick={() => armTool(rayMode ? null : "ray")}
            style={{
              minHeight: "32px", padding: "0 12px", borderRadius: "6px", cursor: "pointer",
              fontSize: "12px", fontWeight: 600,
              background: rayMode ? "rgba(34,211,238,0.18)" : "rgba(255,255,255,0.04)",
              border: rayMode ? "1px solid #22d3ee" : "1px solid rgba(255,255,255,0.1)",
              color: rayMode ? "#22d3ee" : "#aaa",
            }}
          >
            {!rayMode ? "Ray" : rayPhase === "placing" ? "Click to finish" : "Click to start"}
          </button>
        )}
        {userId && myRays.map((r, i) => (
            <span key={r.id} style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
              color: "#ccc", fontSize: "12px",
            }}>
              Ray {i + 1}
              <button
                aria-label="Delete ray"
                onClick={() => deleteRay(r.id)}
                style={{
                  background: "none", border: "none", color: "#888", cursor: "pointer",
                  fontSize: "13px", lineHeight: 1, padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        {userId && myDrawings.map((d) => (
            <span key={d.id} style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
              color: "#ccc", fontSize: "12px",
            }}>
              {d.price.toLocaleString(undefined, { maximumFractionDigits: 4 })}
              <button
                aria-label="Delete line"
                onClick={() => {
                  deleteDrawing(userId, d.id)
                    .then(() => setMyDrawings((prev) => prev.filter((x) => x.id !== d.id)))
                    .catch((e) => console.error("[chart_drawings-delete]", e?.message || e));
                }}
                style={{
                  background: "none", border: "none", color: "#888", cursor: "pointer",
                  fontSize: "13px", lineHeight: 1, padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        {userId && (
          <button
            onClick={() => armTool(longMode ? null : "long")}
            style={{
              minHeight: "32px", padding: "0 12px", borderRadius: "6px", cursor: "pointer",
              fontSize: "12px", fontWeight: 600,
              background: longMode ? "rgba(74,222,128,0.18)" : "rgba(255,255,255,0.04)",
              border: longMode ? "1px solid #4ade80" : "1px solid rgba(255,255,255,0.1)",
              color: longMode ? "#4ade80" : "#aaa",
            }}
          >
            Long Position
          </button>
        )}
        {userId && myPositions.map((p, i) => (
            <span key={p.id} style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
              color: "#ccc", fontSize: "12px",
            }}>
              Long {i + 1}
              <button
                aria-label="Delete long position"
                onClick={() => deletePosition(p.id)}
                style={{
                  background: "none", border: "none", color: "#888", cursor: "pointer",
                  fontSize: "13px", lineHeight: 1, padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
      </div>
      <div style={{ position: "relative", width: "100%", height: "420px" }}>
        <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
        <div ref={priceXhairRef} style={xhairStyle} />
        <div ref={priceLegendRef} style={priceLegendStyle} />
        <MeasureOverlay measurement={measurement} stats={measureStats} paneHeight={420} onClear={clearMeasurement} />
        <TrendLineOverlay lines={trendProjectedLines} />
        <FibOverlay fibs={projectedFibs} />
        <ChannelOverlay channels={projectedChannels} />
        <RayOverlay rays={projectedRays} />
        <LongPositionOverlay positions={projectedPositions} onDelete={deletePosition} />
        {/* Load-failure overlay — stopgap for the rate-limit blank-chart bug (see
            docs/known-issue-rate-limiting-blank-charts.md). Shows instead of a silent
            blank pane; Retry bumps retryTick to re-run the fetch effect. */}
        {loadError && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 6, display: "flex",
            flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px",
            background: "rgba(17,17,17,0.88)",
          }}>
            <span style={{ color: "#f87171", fontSize: "13px" }}>{loadError}</span>
            <button
              onClick={() => setRetryTick((n) => n + 1)}
              style={{
                minHeight: "36px", padding: "0 16px", borderRadius: "6px", cursor: "pointer",
                border: "1px solid rgba(248,113,113,0.4)", background: "rgba(248,113,113,0.12)",
                color: "#f87171", fontSize: "12px", fontWeight: 600,
              }}
            >
              Retry
            </button>
          </div>
        )}
      </div>
      <div style={{ position: "relative", width: "100%", height: showRSI ? "180px" : "0px", overflow: "hidden", marginTop: showRSI ? "2px" : "0px" }}>
        <div ref={rsiContainerRef} style={{ width: "100%", height: "100%" }} />
        <div ref={rsiXhairRef} style={xhairStyle} />
        <div ref={rsiLegendRef} style={priceLegendStyle} />
      </div>
      <div style={{ position: "relative", width: "100%", height: showMACD ? "180px" : "0px", overflow: "hidden", marginTop: showMACD ? "2px" : "0px" }}>
        <div ref={macdContainerRef} style={{ width: "100%", height: "100%" }} />
        <div ref={macdXhairRef} style={xhairStyle} />
        <div ref={macdLegendRef} style={priceLegendStyle} />
      </div>
    </>
  );
}
